import { useState, useCallback, useRef, useEffect } from "react";
import { Pencil } from "lucide-react";
import type { LaunchTask, LaunchStatus, LaunchAssignee } from "@/lib/admin-api";

interface LaunchTaskRowProps {
  readonly task: LaunchTask;
  readonly adminUsers: readonly LaunchAssignee[];
  readonly onToggle: (id: string, newStatus: LaunchStatus) => Promise<void>;
  readonly onAssign: (id: string, userId: string | null) => Promise<void>;
  readonly onEdit: (task: LaunchTask) => void;
}

const PRIORITY_STYLES = {
  critique: "bg-red-100 text-red-700 border-red-200",
  best_practice: "bg-blue-100 text-blue-700 border-blue-200",
} as const;

const PRIORITY_LABELS = {
  critique: "CRITIQUE",
  best_practice: "BEST PRACTICE",
} as const;

const STATUS_STYLES = {
  todo: "bg-stone-100 text-stone-500",
  in_progress: "bg-amber-100 text-amber-700",
  done: "bg-emerald-100 text-emerald-700",
} as const;

const STATUS_LABELS = {
  todo: "À faire",
  in_progress: "En cours",
  done: "Fait",
} as const;

const AVATAR_COLORS = [
  "bg-[#c2642a]",
  "bg-blue-500",
  "bg-emerald-600",
  "bg-violet-500",
  "bg-rose-500",
  "bg-amber-600",
];

function avatarColor(userId: string): string {
  const sum = userId
    .split("")
    .reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return AVATAR_COLORS[sum % AVATAR_COLORS.length];
}

function Initials({
  name,
  colorClass,
}: {
  name: string;
  colorClass: string;
}) {
  const initials = name
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return (
    <span
      className={`inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-[9px] font-bold text-white ${colorClass}`}
    >
      {initials}
    </span>
  );
}

export function LaunchTaskRow({
  task,
  adminUsers,
  onToggle,
  onAssign,
  onEdit,
}: LaunchTaskRowProps) {
  const [assignOpen, setAssignOpen] = useState(false);
  const [toggling, setToggling] = useState(false);
  const assignRef = useRef<HTMLDivElement>(null);

  const handleToggle = useCallback(async () => {
    setToggling(true);
    try {
      const next: LaunchStatus = task.status === "done" ? "todo" : "done";
      await onToggle(task.id, next);
    } finally {
      setToggling(false);
    }
  }, [task.id, task.status, onToggle]);

  const handleAssign = useCallback(
    async (userId: string | null) => {
      setAssignOpen(false);
      await onAssign(task.id, userId);
    },
    [task.id, onAssign],
  );

  useEffect(() => {
    if (!assignOpen) return;
    function handleOutside(e: MouseEvent) {
      if (assignRef.current && !assignRef.current.contains(e.target as Node)) {
        setAssignOpen(false);
      }
    }
    function handleEscape(e: KeyboardEvent) {
      if (e.key === "Escape") setAssignOpen(false);
    }
    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [assignOpen]);

  const isDone = task.status === "done";

  return (
    <tr
      className={`border-b border-stone-100 transition-colors hover:bg-stone-50 ${isDone ? "opacity-60" : ""}`}
    >
      <td className="w-10 px-4 py-3">
        <button
          onClick={handleToggle}
          disabled={toggling}
          aria-label={
            isDone ? "Marquer comme à faire" : "Marquer comme fait"
          }
          className={`flex h-4 w-4 items-center justify-center rounded border-[1.5px] transition-colors ${
            isDone
              ? "border-emerald-500 bg-emerald-500 text-white"
              : "border-stone-300 bg-white hover:border-[#c2642a]"
          }`}
        >
          {isDone && (
            <svg
              viewBox="0 0 12 10"
              className="h-2.5 w-2.5 fill-none stroke-white stroke-[2]"
            >
              <polyline points="1,5 4.5,8.5 11,1" />
            </svg>
          )}
        </button>
      </td>

      <td className="px-2 py-3">
        <p
          className={`text-sm font-semibold text-stone-800 ${isDone ? "line-through" : ""}`}
        >
          {task.title}
        </p>
        {task.description && (
          <p className="mt-0.5 text-xs text-stone-400">{task.description}</p>
        )}
      </td>

      <td className="px-2 py-3">
        <span
          className={`inline-block rounded border px-1.5 py-0.5 text-[9px] font-bold tracking-wide ${PRIORITY_STYLES[task.priority]}`}
        >
          {PRIORITY_LABELS[task.priority]}
        </span>
      </td>

      <td className="px-2 py-3">
        <div className="relative">
          <button
            onClick={() => setAssignOpen((v) => !v)}
            aria-expanded={assignOpen}
            aria-haspopup="listbox"
            className="flex items-center gap-1.5 rounded px-1 py-0.5 text-xs text-stone-600 hover:bg-stone-100"
          >
            {task.assignee ? (
              <>
                <Initials
                  name={task.assignee.full_name}
                  colorClass={avatarColor(task.assignee.id)}
                />
                <span>{task.assignee.full_name.split(" ")[0]}</span>
              </>
            ) : (
              <span
                className={`italic text-stone-300 ${!isDone ? "rounded border border-dashed border-[#c2642a]/40 px-1.5 py-0.5 text-[#c2642a]" : ""}`}
              >
                — Non assigné
              </span>
            )}
          </button>

          {assignOpen && (
            <div ref={assignRef} className="absolute left-0 top-full z-10 mt-1 w-48 rounded-lg border border-stone-200 bg-white py-1 shadow-lg">
              <button
                onClick={() => handleAssign(null)}
                className="block w-full px-3 py-1.5 text-left text-xs text-stone-500 hover:bg-stone-50"
              >
                — Retirer l'assignation
              </button>
              {adminUsers.map((u) => (
                <button
                  key={u.id}
                  onClick={() => handleAssign(u.id)}
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-stone-700 hover:bg-stone-50"
                >
                  <Initials
                    name={u.full_name}
                    colorClass={avatarColor(u.id)}
                  />
                  {u.full_name}
                </button>
              ))}
            </div>
          )}
        </div>
      </td>

      <td className="px-2 py-3">
        <span
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold ${STATUS_STYLES[task.status]}`}
        >
          <span className="h-1.5 w-1.5 rounded-full bg-current" />
          {STATUS_LABELS[task.status]}
        </span>
      </td>

      <td className="px-2 py-3">
        <button
          onClick={() => onEdit(task)}
          aria-label="Modifier la tâche"
          title="Modifier"
          className="rounded border border-stone-200 bg-white px-2 py-1 text-stone-500 hover:border-stone-300 hover:bg-stone-50"
        >
          <Pencil className="h-3 w-3" />
        </button>
      </td>
    </tr>
  );
}
